/**
 * UserDepartments Component
 * Displays and manages a user's department memberships with multi-department support
 */

import React, { useState } from 'react';
import { Star, Trash2, Plus } from 'lucide-react';
import { Card, Button, Badge, Select, LoadingSpinner } from '../common';
import {
  useUserDepartments,
  useUpdateUserDepartment,
  useRemoveUserDepartment,
  checkAffectedOKRs,
} from '../../hooks';
import { AddDepartmentModal } from './AddDepartmentModal';

export interface UserDepartmentsProps {
  userId: string;
  canEdit?: boolean;
}

export const UserDepartments: React.FC<UserDepartmentsProps> = ({ userId, canEdit = true }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [departmentToRemove, setDepartmentToRemove] = useState<string | null>(null);

  const { data: departments = [], isLoading } = useUserDepartments(userId);
  const updateMutation = useUpdateUserDepartment();
  const removeMutation = useRemoveUserDepartment();

  // Get role badge variant
  const getRoleBadgeVariant = (role: string | null): 'primary' | 'warning' | 'secondary' => {
    if (!role) return 'secondary';
    switch (role.toLowerCase()) {
      case 'manager':
        return 'primary';
      case 'lead':
        return 'warning';
      case 'contributor':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  // Handle set as primary
  const handleSetPrimary = async (departmentId: string) => {
    try {
      await updateMutation.mutateAsync({
        userId,
        departmentId,
        data: { isPrimary: true },
      });
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  // Handle role change
  const handleRoleChange = async (departmentId: string, newRole: string) => {
    try {
      await updateMutation.mutateAsync({
        userId,
        departmentId,
        data: { role: newRole || null },
      });
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  // Handle remove department - checks for affected OKRs first
  const handleRemove = async (departmentId: string, departmentName: string) => {
    try {
      setDepartmentToRemove(departmentId);

      // Check for affected OKRs first
      const affectedOKRs = await checkAffectedOKRs(userId, departmentId);

      let confirmMessage: string;
      let shouldForce = false;

      if (affectedOKRs.count > 0) {
        // Show detailed warning about affected OKRs
        const okrList = affectedOKRs.okrs.slice(0, 3).map(okr => `  • ${okr.title}`).join('\n');
        const moreText = affectedOKRs.count > 3 ? `\n  ... and ${affectedOKRs.count - 3} more` : '';

        confirmMessage =
          `WARNING: ${affectedOKRs.count} OKR(s) are associated with "${departmentName}":\n\n` +
          `${okrList}${moreText}\n\n` +
          `If you proceed, these OKRs will be automatically reassigned to the user's primary department.\n\n` +
          `Do you want to continue?`;
        shouldForce = true;
      } else {
        confirmMessage = `Are you sure you want to remove this user from "${departmentName}"?\n\nThis action cannot be undone.`;
      }

      const confirmed = window.confirm(confirmMessage);

      if (confirmed) {
        await removeMutation.mutateAsync({ userId, departmentId, force: shouldForce });
      }
    } catch (error) {
      // Error handling is done in the hook
    } finally {
      setDepartmentToRemove(null);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </Card>
    );
  }

  if (departments.length === 0) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-stone-900">Departments</h3>
          {canEdit && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setIsAddModalOpen(true)}
              icon={Plus}
            >
              Add Department
            </Button>
          )}
        </div>
        <div className="text-center py-8">
          <p className="text-stone-500 mb-4">This user is not assigned to any departments.</p>
          {canEdit && (
            <Button variant="outline" onClick={() => setIsAddModalOpen(true)} icon={Plus}>
              Add to Department
            </Button>
          )}
        </div>
        <AddDepartmentModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          userId={userId}
          existingDepartmentIds={[]}
        />
      </Card>
    );
  }

  const primaryDepartment = departments.find((d) => d.isPrimary);
  const otherDepartments = departments.filter((d) => !d.isPrimary);

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-stone-900">Departments</h3>
          {canEdit && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setIsAddModalOpen(true)}
              icon={Plus}
            >
              Add Department
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {/* Primary Department */}
          {primaryDepartment && (
            <div className="border-2 border-primary-200 bg-primary-50 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="h-4 w-4 text-primary-600 fill-primary-600" />
                    <h4 className="font-semibold text-stone-900">
                      {primaryDepartment.departmentName}
                    </h4>
                    <Badge variant="primary" size="sm">
                      Primary
                    </Badge>
                  </div>

                  {canEdit ? (
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-stone-600">Role:</label>
                      <Select
                        value={primaryDepartment.role || ''}
                        onChange={(e) =>
                          handleRoleChange(primaryDepartment.departmentId, e.target.value)
                        }
                        options={[
                          { value: '', label: 'No Role', disabled: false },
                          { value: 'contributor', label: 'Contributor', disabled: false },
                          { value: 'lead', label: 'Lead', disabled: false },
                          { value: 'manager', label: 'Manager', disabled: false },
                        ]}
                        className="w-40 text-sm"
                        disabled={updateMutation.isPending}
                      />
                    </div>
                  ) : primaryDepartment.role ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-stone-600">Role:</span>
                      <Badge variant={getRoleBadgeVariant(primaryDepartment.role)} size="sm">
                        {primaryDepartment.role}
                      </Badge>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* Other Departments */}
          {otherDepartments.map((dept) => (
            <div
              key={dept.id}
              className="border border-stone-200 bg-white rounded-lg p-4 hover:border-stone-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-stone-900 mb-2">{dept.departmentName}</h4>

                  <div className="flex flex-wrap items-center gap-2">
                    {canEdit ? (
                      <>
                        <label className="text-sm text-stone-600">Role:</label>
                        <Select
                          value={dept.role || ''}
                          onChange={(e) => handleRoleChange(dept.departmentId, e.target.value)}
                          options={[
                            { value: '', label: 'No Role', disabled: false },
                            { value: 'contributor', label: 'Contributor', disabled: false },
                            { value: 'lead', label: 'Lead', disabled: false },
                            { value: 'manager', label: 'Manager', disabled: false },
                          ]}
                          className="w-40 text-sm"
                          disabled={updateMutation.isPending}
                        />
                      </>
                    ) : dept.role ? (
                      <>
                        <span className="text-sm text-stone-600">Role:</span>
                        <Badge variant={getRoleBadgeVariant(dept.role)} size="sm">
                          {dept.role}
                        </Badge>
                      </>
                    ) : null}
                  </div>
                </div>

                {canEdit && (
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetPrimary(dept.departmentId)}
                      disabled={updateMutation.isPending}
                      className="text-xs"
                    >
                      <Star className="h-3 w-3 mr-1" />
                      Set Primary
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemove(dept.departmentId, dept.departmentName)}
                      disabled={
                        removeMutation.isPending && departmentToRemove === dept.departmentId
                      }
                      className="text-xs"
                    >
                      {removeMutation.isPending && departmentToRemove === dept.departmentId ? (
                        <LoadingSpinner size="sm" className="h-3 w-3" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-stone-200">
          <p className="text-xs text-stone-500">
            {departments.length === 1
              ? 'This user belongs to 1 department'
              : `This user belongs to ${departments.length} departments`}
          </p>
        </div>
      </Card>

      <AddDepartmentModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        userId={userId}
        existingDepartmentIds={departments.map((d) => d.departmentId)}
      />
    </>
  );
};
